/**
 * Schedule Report Dialog Component (Reports v2)
 *
 * Configure a recurring email delivery for a dashboard via
 * POST /reporting/schedules/. Supports daily / weekly / monthly /
 * quarterly cadences, hour-of-day, timezone, multi-format output and
 * a per-recipient table.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  type AnalyseDashboard,
  type ScheduleFormat,
  type ScheduleFrequency,
  type ScheduledReportCreatePayload,
  type ScheduledReportRecipient,
} from '../../../lib/api/reporting';

interface ScheduleReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
  saving: boolean;
  onSubmit: (payload: ScheduledReportCreatePayload) => void;
}

const FREQUENCIES: Array<{ value: ScheduleFrequency; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const TIMEZONES = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const ALL_FORMATS: Array<{ value: ScheduleFormat; label: string }> = [
  { value: 'excel', label: 'Excel (.xlsx)' },
  { value: 'pdf', label: 'PDF' },
  { value: 'png', label: 'Chart Image (PNG)' },
  { value: 'csv', label: 'CSV' },
];

export function ScheduleReportDialog({
  open,
  onOpenChange,
  report,
  saving,
  onSubmit,
}: ScheduleReportDialogProps) {
  const [frequency, setFrequency] = useState<ScheduleFrequency>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState<number>(0);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [hour, setHour] = useState<number>(9);
  const [timezone, setTimezone] = useState<string>('Europe/London');
  const [formats, setFormats] = useState<ScheduleFormat[]>(['excel']);
  const [recipients, setRecipients] = useState<ScheduledReportRecipient[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    setFrequency('weekly');
    setDayOfWeek(0);
    setDayOfMonth(1);
    setHour(9);
    setTimezone('Europe/London');
    setFormats(['excel']);
    setRecipients([]);
    setNewEmail('');
    setNewName('');
  }, [open]);

  const toggleFormat = (fmt: ScheduleFormat, checked: boolean) => {
    setFormats((prev) => (checked ? [...prev, fmt] : prev.filter((f) => f !== fmt)));
  };

  const handleAddRecipient = () => {
    const email = newEmail.trim();
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    if (recipients.some((r) => r.email.toLowerCase() === email.toLowerCase())) return;
    setRecipients((prev) => [...prev, { email, name: newName.trim() || undefined }]);
    setNewEmail('');
    setNewName('');
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const canSave = useMemo(
    () => recipients.length > 0 && formats.length > 0 && !!report,
    [recipients.length, formats.length, report],
  );

  const handleSubmit = () => {
    if (!report || !canSave) return;
    const payload: ScheduledReportCreatePayload = {
      dashboard: report.id,
      frequency,
      day_of_week: frequency === 'weekly' ? dayOfWeek : null,
      day_of_month:
        frequency === 'monthly' || frequency === 'quarterly' ? dayOfMonth : null,
      hour,
      timezone,
      formats,
      is_active: true,
      recipients,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Report Email</DialogTitle>
          <DialogDescription>
            Send "{report?.name}" automatically to recipients on a recurring schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Frequency + day */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Frequency *</Label>
              <Select
                value={frequency}
                onValueChange={(value) => setFrequency(value as ScheduleFrequency)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Day of Week *</Label>
                <Select
                  value={dayOfWeek.toString()}
                  onValueChange={(value) => setDayOfWeek(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d) => (
                      <SelectItem key={d.value} value={d.value.toString()}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(frequency === 'monthly' || frequency === 'quarterly') && (
              <div className="space-y-2">
                <Label>Day of Month *</Label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Math.min(28, Math.max(1, parseInt(e.target.value) || 1)))}
                />
              </div>
            )}
          </div>

          {/* Hour + timezone */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hour (24h) *</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={hour}
                onChange={(e) => setHour(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Formats */}
          <div className="space-y-2">
            <Label>Attachment Formats *</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_FORMATS.map((fmt) => (
                <label
                  key={fmt.value}
                  className="flex items-center gap-2 p-2 border rounded-md cursor-pointer hover:bg-muted"
                >
                  <Checkbox
                    checked={formats.includes(fmt.value)}
                    onCheckedChange={(checked) => toggleFormat(fmt.value, checked === true)}
                  />
                  <span className="text-sm">{fmt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div className="space-y-2 pt-2 border-t">
            <Label>Recipients *</Label>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                type="email"
                placeholder="email@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRecipient();
                  }
                }}
              />
              <Input
                placeholder="Name (optional)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRecipient();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={handleAddRecipient}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            <div className="border rounded-md">
              {recipients.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  No recipients yet. Add at least one email above.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-[50px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.map((r) => (
                      <TableRow key={r.email}>
                        <TableCell className="text-sm">{r.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.name || '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleRemoveRecipient(r.email)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSave || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
